/**
 * Context Orchestrator RAG Enhancement
 * 
 * Extends the base context orchestrator with semantic search capabilities.
 * Add this to your existing contextOrchestrator.ts or use as a wrapper.
 * 
 * Usage:
 * import { contextOrchestratorRAG } from './contextOrchestratorRAG';
 * 
 * const context = await contextOrchestratorRAG.getEnhancedContext(threadId, {
 *   useRAG: true,
 *   lastUserMessage: "What are the project requirements?",
 *   ragOptions: { limit: 5, similarityThreshold: 0.7 }
 * });
 */

import { ragService } from './ragService';
import { integrationRagService } from './integrationRagService';
import { resolveSpaceTopicNames } from './spaceTopicNameResolver';
import type { ContextPayload, SourceContext } from './contextOrchestrator';
import { partialScopeNotice, type ScopeBreadth, type ScopeCoverage, type ScopeKind } from '@shared/scopeRag';

export interface RAGSourceContext extends SourceContext {
  similarity?: number;
  retrievalMethod: 'semantic' | 'explicit';
  /** Stable id for deduping RAG chunks vs explicit sources */
  ragDocumentId?: string;
  ragThreadId?: string;
}

export interface RAGOptions {
  enabled?: boolean;
  limit?: number;
  similarityThreshold?: number;
  hybridMode?: boolean; // Combine explicit + semantic sources
  hybridSearch?: boolean; // Combine keyword (FTS) + vector search
  useRerank?: boolean; // Rerank top-K via Cohere (requires COHERE_API_KEY)
  /** Passed to ragService.search for direct-by-title retrieval (Knowledge Base manifest) */
  topicDocumentManifest?: Array<{ id: string; title: string }>;
  /** Thread titles in Knowledge scope for direct-by-title + expand */
  threadManifest?: Array<{ id: string; title: string }>;
  /** DS EDIT 5 — multi-workspace RAG scope (thread-persisted; auto-loaded in getEnhancedContext) */
  ragWorkspaceIds?: string[];
  /** When set, open chunks only for these shelf ids (documents, thread:{id}, source:externalId). */
  shelfIds?: string[];
  /** Default retrieval. entire packs a bounded sample across every resource in the selected scope. */
  scopeBreadth?: ScopeBreadth;
}

export interface EnhancedContextPayload extends ContextPayload {
  ragSources: RAGSourceContext[];
  retrievalMetadata: {
    usedRAG: boolean;
    semanticSourcesCount: number;
    explicitSourcesCount: number;
    avgSimilarity: number;
    contextCoverage?: ScopeCoverage;
  };
}

class ContextOrchestratorRAGService {
  /**
   * Get semantic sources based on the user's query (documents and optionally thread chunks when topicId set)
   */
  private async getSemanticSources(
    query: string,
    spaceId?: string | null,
    topicId?: string | null,
    options: RAGOptions = {}
  ): Promise<RAGSourceContext[]> {
    const {
      limit = 5,
      similarityThreshold = 0.6,
      hybridSearch = true,
      useRerank = false,
      topicDocumentManifest = [],
      threadManifest = [],
      ragWorkspaceIds = [],
      scopeBreadth = 'retrieval',
    } = options;

    try {
      const [results, integrationResults] = await Promise.all([
        ragService.search(query, {
          spaceId: spaceId || undefined,
          topicId: topicId || undefined,
          limit,
          similarityThreshold,
          hybridSearch,
          useRerank,
          includeThreads: !!(spaceId || topicId),
          topicDocumentManifest,
          threadManifest,
          workspaceIds: ragWorkspaceIds,
          scopeBreadth,
        }),
        integrationRagService.search(query, {
          limit,
          similarityThreshold,
        }),
      ]);

      // Resolve space/topic names from chunk metadata so retrieved sources show their origin
      const { spaceNames, topicNames } = await resolveSpaceTopicNames(
        results.map((r) => r.metadata?.spaceId as string | undefined),
        results.map((r) => r.metadata?.topicId as string | undefined)
      );

      const docSources: RAGSourceContext[] = results.map((result, index) => {
        const metaSpaceId = result.metadata?.spaceId as string | undefined;
        const metaTopicId = result.metadata?.topicId as string | undefined;
        return {
          id: result.metadata?.citationIdentity
            ? `rag_${String(result.metadata.citationIdentity)}_${index}`
            : result.threadId
              ? `rag_thread_${result.threadId}_${index}`
              : `rag_${result.documentId}_${index}`,
          title: result.threadTitle ?? result.documentTitle ?? 'Retrieved',
          content: result.chunkText,
          type: 'semantic_retrieval',
          citationIndex: 0,
          similarity: result.similarity,
          retrievalMethod: 'semantic' as const,
          url: null,
          ragDocumentId: result.documentId,
          ragThreadId: result.threadId,
          spaceName: metaSpaceId ? spaceNames.get(metaSpaceId) : undefined,
          topicName: metaTopicId ? topicNames.get(metaTopicId) : undefined,
        };
      });

      const shelfIds = options.shelfIds;
      const allowedDocs = shelfIds
        ? docSources.filter((source) =>
            (source.ragDocumentId && shelfIds.includes(source.ragDocumentId)) ||
            (source.ragThreadId && shelfIds.includes(`thread:${source.ragThreadId}`)),
          )
        : docSources;
      const expandedFlags = new Set(
        results
          .map((r, i) => (r.metadata?.fullContentExpanded ? i : -1))
          .filter((i) => i >= 0)
      );
      const expandedSources = allowedDocs.filter((source) => expandedFlags.has(docSources.indexOf(source)));
      const otherDocSources = allowedDocs.filter((source) => !expandedFlags.has(docSources.indexOf(source)));

      const integrationSources: RAGSourceContext[] = integrationResults.map(
        (result, index) => ({
          id: `rag_integration_${result.source}_${result.externalId}_${result.chunkIndex}_${index}`,
          title: result.title,
          content: result.chunkText,
          type: 'semantic_retrieval',
          citationIndex: 0,
          similarity: result.similarity,
          retrievalMethod: 'semantic' as const,
          url: null,
          ragDocumentId: `${result.source}:${result.externalId}`,
        })
      );
      const allowedIntegration = shelfIds
        ? integrationSources.filter((source) => source.ragDocumentId && shelfIds.includes(source.ragDocumentId))
        : integrationSources;

      // Keep fully expanded bodies first so integration chunks cannot evict them
      const cap = scopeBreadth === 'entire' ? Math.max(results.length, limit, 10) : Math.max(limit, 10);
      const remainingSlots = Math.max(0, cap - expandedSources.length);
      const fillers = [...otherDocSources, ...allowedIntegration]
        .sort((a, b) => (b.similarity ?? 0) - (a.similarity ?? 0))
        .slice(0, remainingSlots);

      return [...expandedSources, ...fillers];
    } catch (error) {
      console.error('Error retrieving semantic sources:', error);
      return [];
    }
  }

  /**
   * Merge and deduplicate explicit and semantic sources
   */
  private mergeSourcesIntelligently(
    explicitSources: SourceContext[],
    semanticSources: RAGSourceContext[]
  ): RAGSourceContext[] {
    const merged: RAGSourceContext[] = [];
    const seenDocIds = new Set<string>();

    // Add explicit sources first (higher priority)
    for (const source of explicitSources) {
      merged.push({
        ...source,
        retrievalMethod: 'explicit',
        similarity: 1.0 // Explicit sources have max relevance
      } as RAGSourceContext);
      seenDocIds.add(source.id);
    }

    // Add semantic sources that aren't duplicates (thread chunks always added; doc chunks skip if already explicit)
    for (const source of semanticSources) {
      const isThread = Boolean(source.ragThreadId);
      const docId = isThread ? null : source.ragDocumentId ?? source.id.split('_')[1];
      if (isThread || !docId || !seenDocIds.has(docId)) {
        merged.push(source);
        if (!isThread && docId) seenDocIds.add(docId);
      }
    }

    // Sort by similarity (explicit sources already at top with 1.0)
    return merged.sort((a, b) => (b.similarity || 0) - (a.similarity || 0));
  }

  /**
   * When Knowledge scope is active, do not dump every space/topic body into the prompt.
   * Keep SSOT / attachments / linked only — not topic_meta stubs (topic name as fake body).
   */
  private getLightweightExplicitForScopedRag(base: ContextPayload): SourceContext[] {
    const keepIds = new Set<string>();
    for (const s of [
      ...base.sourceOfTruthDocuments,
      ...base.attachedDocuments,
      ...base.threadAttachments,
      ...base.linkedDocuments,
    ]) {
      keepIds.add(s.id);
    }
    return base.allSources.filter(
      (s) =>
        keepIds.has(s.id) &&
        !s.id.startsWith('topic_meta_') &&
        s.type !== 'topic'
    );
  }

  /**
   * Get enhanced context for a new thread (no threadId yet).
   * Fetches base context from getContextForNewThread, then adds semantic retrieval if useRAG and lastUserMessage.
   */
  async getEnhancedContextForNewThread(
    options: {
      spaceId?: string | null;
      topicId?: string | null;
      personalizationEnabled?: boolean;
      agentId?: string | null;
      superPromptId?: string | null;
      superPromptVariables?: Record<string, string>;
      maxTokens?: number;
      selectedDocumentIds?: string[];
      useRAG?: boolean;
      lastUserMessage?: string;
      ragOptions?: RAGOptions;
    } = {}
  ): Promise<EnhancedContextPayload> {
    const {
      useRAG = true,
      lastUserMessage = '',
      ragOptions = {},
      ...newThreadOptions
    } = options;

    const { contextOrchestrator } = await import('./contextOrchestrator');
    // Base context includes space/topic docs (explicit); when RAG is on, semantic retrieval adds embedded chunks.
    const baseContext = await contextOrchestrator.getContextForNewThread(newThreadOptions);

    const basePayload: ContextPayload = { ...baseContext, threadId: '' };

    if (!useRAG || !lastUserMessage || lastUserMessage.trim().length === 0) {
      return {
        ...basePayload,
        ragSources: baseContext.allSources.map(s => ({
          ...s,
          retrievalMethod: 'explicit' as const,
          similarity: 1.0
        })),
        retrievalMetadata: {
          usedRAG: false,
          semanticSourcesCount: 0,
          explicitSourcesCount: baseContext.allSources.length,
          avgSimilarity: 1.0
        }
      };
    }

    const scoped = !!(baseContext.spaceId || baseContext.topicId);
    const manifestForRagNew =
      baseContext.topicDocumentManifest?.map((m) => ({ id: m.id, title: m.title })) ?? [];
    const threadManifestForRagNew =
      baseContext.threadManifest?.map((m) => ({ id: m.id, title: m.title })) ?? [];
    const semanticSources = await this.getSemanticSources(
      lastUserMessage,
      baseContext.spaceId,
      baseContext.topicId ?? undefined,
      {
        ...ragOptions,
        topicDocumentManifest: manifestForRagNew,
        threadManifest: threadManifestForRagNew,
      }
    );

    const explicitForMerge = scoped
      ? this.getLightweightExplicitForScopedRag(baseContext)
      : baseContext.allSources;

    let mergedSources = ragOptions.hybridMode !== false
      ? this.mergeSourcesIntelligently(explicitForMerge, semanticSources)
      : semanticSources;
    if (ragOptions.shelfIds) {
      const shelves = ragOptions.shelfIds;
      mergedSources = mergedSources.filter((source) =>
        (source.ragDocumentId && shelves.includes(source.ragDocumentId)) ||
        (source.ragThreadId && shelves.includes(`thread:${source.ragThreadId}`)) ||
        (source.id && shelves.includes(source.id)),
      );
    }

    let citationIndex = 1;
    const citationMap = new Map<number, SourceContext>();
    for (const source of mergedSources) {
      source.citationIndex = citationIndex;
      citationMap.set(citationIndex, source);
      citationIndex++;
    }

    const semanticCount = mergedSources.filter(s => s.retrievalMethod === 'semantic').length;
    const explicitCount = mergedSources.filter(s => s.retrievalMethod === 'explicit').length;
    const avgSimilarity = mergedSources.reduce((sum, s) => sum + (s.similarity || 0), 0) / mergedSources.length;

    return {
      ...basePayload,
      allSources: mergedSources,
      citationMap,
      ragSources: mergedSources,
      retrievalMetadata: {
        usedRAG: true,
        semanticSourcesCount: semanticCount,
        explicitSourcesCount: explicitCount,
        avgSimilarity,
        contextCoverage: ragService.getLastScopeRetrieval() ?? undefined,
      },
      metadata: {
        ...basePayload.metadata,
        totalSources: mergedSources.length
      }
    };
  }

  /**
   * Enhanced context retrieval with RAG
   * 
   * This is the drop-in replacement for contextOrchestrator.getContextForThread()
   * that adds semantic retrieval on top of explicit sources.
   */
  async getEnhancedContext(
    threadId: string,
    options: {
      personalizationEnabled?: boolean;
      agentId?: string | null;
      superPromptId?: string | null;
      superPromptVariables?: Record<string, string>;
      workflowId?: string | null;
      workflowNodeId?: string | null;
      workflowVariables?: Record<string, unknown>;
      maxTokens?: number;
      selectedDocumentIds?: string[];
      /** UI Knowledge scope overrides */
      spaceId?: string | null;
      topicId?: string | null;
      
      // RAG-specific options
      useRAG?: boolean;
      lastUserMessage?: string;
      ragOptions?: RAGOptions;
    } = {}
  ): Promise<EnhancedContextPayload> {
    const {
      useRAG = true,
      lastUserMessage = '',
      ragOptions = {},
    } = options;

    // Import contextOrchestrator dynamically to avoid circular dependencies
    const { contextOrchestrator } = await import('./contextOrchestrator');

    // Base context includes explicit sources. spaceId/topicId from Chat Tools override thread links.
    const baseContext = await contextOrchestrator.getContextForThread(threadId, options);

    // If RAG is disabled or no query, return base context (docs still included via explicit sources)
    if (!useRAG || !lastUserMessage || lastUserMessage.trim().length === 0) {
      return {
        ...baseContext,
        ragSources: baseContext.allSources.map(s => ({
          ...s,
          retrievalMethod: 'explicit' as const,
          similarity: 1.0
        })),
        retrievalMetadata: {
          usedRAG: false,
          semanticSourcesCount: 0,
          explicitSourcesCount: baseContext.allSources.length,
          avgSimilarity: 1.0
        }
      };
    }

    const scoped = !!(baseContext.spaceId || baseContext.topicId);
    const manifestForRag =
      baseContext.topicDocumentManifest?.map((m) => ({ id: m.id, title: m.title })) ?? [];
    const threadManifestForRag =
      baseContext.threadManifest?.map((m) => ({ id: m.id, title: m.title })) ?? [];

    // DS EDIT 5 — load the thread's persisted multi-workspace scope unless the
    // caller supplied one. Load failure degrades to single-workspace (the
    // backward-compatible default); retrieval itself stays fail-closed.
    let ragWorkspaceIds = ragOptions.ragWorkspaceIds;
    if (ragWorkspaceIds === undefined) {
      try {
        const { ragScopeService } = await import('./ragScopeService');
        ragWorkspaceIds = (await ragScopeService.getThreadScope(threadId)) ?? undefined;
      } catch {
        ragWorkspaceIds = undefined;
      }
    }

    const semanticSources = await this.getSemanticSources(
      lastUserMessage,
      baseContext.spaceId,
      baseContext.topicId ?? undefined,
      {
        ...ragOptions,
        topicDocumentManifest: manifestForRag,
        threadManifest: threadManifestForRag,
        ragWorkspaceIds,
      }
    );

    const explicitForMerge = scoped
      ? this.getLightweightExplicitForScopedRag(baseContext)
      : baseContext.allSources;

    // Merge sources intelligently
    let mergedSources = ragOptions.hybridMode !== false
      ? this.mergeSourcesIntelligently(explicitForMerge, semanticSources)
      : semanticSources; // Pure RAG mode
    if (ragOptions.shelfIds) {
      const shelves = ragOptions.shelfIds;
      mergedSources = mergedSources.filter((source) =>
        (source.ragDocumentId && shelves.includes(source.ragDocumentId)) ||
        (source.ragThreadId && shelves.includes(`thread:${source.ragThreadId}`)) ||
        (source.id && shelves.includes(source.id)),
      );
    }

    // Assign citation indices
    let citationIndex = 1;
    const citationMap = new Map<number, SourceContext>();
    for (const source of mergedSources) {
      source.citationIndex = citationIndex;
      citationMap.set(citationIndex, source);
      citationIndex++;
    }

    // Calculate metadata
    const semanticCount = mergedSources.filter(s => s.retrievalMethod === 'semantic').length;
    const explicitCount = mergedSources.filter(s => s.retrievalMethod === 'explicit').length;
    const avgSimilarity = mergedSources.reduce((sum, s) => sum + (s.similarity || 0), 0) / mergedSources.length;

    return {
      ...baseContext,
      allSources: mergedSources,
      citationMap,
      ragSources: mergedSources,
      retrievalMetadata: {
        usedRAG: true,
        semanticSourcesCount: semanticCount,
        explicitSourcesCount: explicitCount,
        avgSimilarity,
        contextCoverage: ragService.getLastScopeRetrieval() ?? undefined,
      },
      metadata: {
        ...baseContext.metadata,
        totalSources: mergedSources.length
      }
    };
  }

  /**
   * Generate system prompt with RAG context
   * 
   * Creates an enhanced system prompt that includes retrieved sources
   * with clear attribution and instructions.
   */
  generateRAGSystemPrompt(
    context: EnhancedContextPayload,
    baseSystemPrompt: string = 'You are a helpful AI assistant.'
  ): string {
    if (context.ragSources.length === 0) {
      return baseSystemPrompt;
    }

    const sourcesSections: string[] = [];

    // "Title — Space: X · Topic: Y" when space/topic names are known
    const sourceHeading = (source: RAGSourceContext): string => {
      const origin = [
        source.spaceName ? `Space: ${source.spaceName}` : null,
        source.topicName ? `Topic: ${source.topicName}` : null,
      ]
        .filter(Boolean)
        .join(' · ');
      return origin ? `${source.title} — ${origin}` : source.title;
    };

    // Group sources by retrieval method
    const explicitSources = context.ragSources.filter(s => s.retrievalMethod === 'explicit');
    const semanticSources = context.ragSources.filter(s => s.retrievalMethod === 'semantic');

    if (explicitSources.length > 0) {
      sourcesSections.push('## Explicitly Attached Sources\n');
      explicitSources.forEach(source => {
        sourcesSections.push(
          `[${source.citationIndex}] ${sourceHeading(source)}\n${source.content}\n`
        );
      });
    }

    if (semanticSources.length > 0) {
      sourcesSections.push('\n## Semantically Retrieved Sources\n');
      sourcesSections.push(
        '(Full document or thread bodies for the most relevant matches in the Knowledge scope)\n'
      );
      semanticSources.forEach(source => {
        const relevance = ((source.similarity || 0) * 100).toFixed(0);
        sourcesSections.push(
          `[${source.citationIndex}] ${sourceHeading(source)} (${relevance}% relevant)\n${source.content}\n`
        );
      });
    }

    const contextSection = sourcesSections.join('\n');

    const coverage = context.retrievalMetadata.contextCoverage;
    const scopeKind: ScopeKind | null = coverage?.scope_type === 'topic' || coverage?.scope_type === 'space'
      ? coverage.scope_type
      : null;
    const partial = scopeKind && coverage ? partialScopeNotice(scopeKind, coverage) : null;
    const partialBlock = partial ? `\n${partial}\n` : '';

    return `${baseSystemPrompt}

# Retrieved Context (User's Knowledge Base)

The following content was retrieved from the user's own documents and chat threads. When this context is relevant to the question, you MUST base your answer on it and cite it using [citation_number]. Do not prefer web search or generic knowledge over this retrieved context when it answers the question.
${partialBlock}
${contextSection}

# Instructions

1. PRIORITY: Use the retrieved context above to answer. If it contains relevant information, base your answer on it and cite [1], [2], etc. Do not substitute web search or generic content when the user's documents already answer the question.
2. Cite sources using [1], [2], etc. when referencing information from the retrieved context.
3. If the retrieved context does not contain relevant information, say so and only then use other knowledge or web results.
4. Prioritize information from higher-ranked sources (they're more relevant).
5. Knowledge inventory lists titles of documents and threads in scope. Relevant full bodies are loaded into the retrieved sections above automatically — use those bodies; do not ask the user to restate a title just to unlock content when retrieved sources already cover the question.
`;
  }

  /**
   * Format sources for display in UI
   */
  formatSourcesForUI(context: EnhancedContextPayload): Array<{
    id: string;
    title: string;
    type: string;
    similarity?: number;
    retrievalMethod: string;
    citationNumber: number;
  }> {
    return context.ragSources.map(source => ({
      id: source.id,
      title: source.title,
      type: source.type,
      similarity: source.similarity,
      retrievalMethod: source.retrievalMethod,
      citationNumber: source.citationIndex
    }));
  }
}

// Export singleton
export const contextOrchestratorRAG = new ContextOrchestratorRAGService();
